import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { ArrowLeft, BookOpen, Send, Scissors, ChevronRight, Activity, Plus, Clock, FileText } from 'lucide-react';
import AnalyzedResult from '../components/AnalyzedResult';
import MeaningPopup from '../components/MeaningPopup';
import { useSmartTTS } from '../hooks/useSmartTTS';

const Container = styled.div`
  min-height: 100vh;
  background: #141414;
  color: #e5e5e5;
  padding: 20px;
  max-width: 680px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  position: sticky;
  top: 0;
  background: #141414;
  z-index: 10;
  padding: 10px 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  &:hover { background: #333; }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  flex: 1;
`;

const Card = styled.div`
  background: #1f1f1f;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #333;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #888;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  margin-bottom: 20px;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  margin-bottom: 20px;
  font-size: 16px;
  box-sizing: border-box;
`;

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;

const TopicPill = styled.button`
  padding: 8px 12px;
  background: ${props => props.$active ? '#e50914' : '#2a2a2a'};
  border: 1px solid ${props => props.$active ? '#e50914' : '#444'};
  border-radius: 20px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${props => props.$active ? '#e50914' : '#3d3d3d'}; }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #e50914;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  &:disabled { background: #555; cursor: not-allowed; }
`;

const SecondaryButton = styled(Button)`
  background: #333;
  &:hover { background: #444; }
`;

const FullText = styled.div`
  white-space: pre-wrap;
  line-height: 1.8;
  font-size: 17px;
  color: #e5e5e5;
  margin-bottom: 24px;
`;

const SplitControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  background: #222;
  padding: 12px;
  border-radius: 12px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  background: #1f1f1f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #e50914; background: #262626; }
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: #e50914;
  font-size: 13px;
  font-weight: 600;
`;

const ListText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #ccc;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const AnalysisSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

function MyStoryPage() {
    const navigate = useNavigate();
    const { speak } = useSmartTTS();
    const [step, setStep] = useState('history'); // history -> genre -> topic -> config -> result -> list -> detail
    const [loading, setLoading] = useState(false);

    // History State
    const [history, setHistory] = useState([]);
    const [currentStoryId, setCurrentStoryId] = useState(null);

    // Selection States
    const [genre, setGenre] = useState('');
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState('');

    // Config States
    const [config, setConfig] = useState({
        mood: 'Exciting',
        targetAge: 'Adult',
        spatialBackground: 'Global',
        temporalBackground: 'Present',
        keywords: ''
    });

    // Result States
    const [storyContent, setStoryContent] = useState('');
    const [splitSize, setSplitSize] = useState(10);
    const [chunks, setChunks] = useState([]);
    const [selectedChunk, setSelectedChunk] = useState(null);

    // Analysis States
    const [analysisData, setAnalysisData] = useState(null);
    const [analysisDocId, setAnalysisDocId] = useState(null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [popup, setPopup] = useState(null);
    const [wordInfo, setWordInfo] = useState(null);
    const [loadingWord, setLoadingWord] = useState(false);

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user?.id) return;

        setLoading(true);
        try {
            const res = await apiService.getUserStories(user.id);
            setHistory(res.stories || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadTopics = async (selectedGenre) => {
        setLoading(true);
        try {
            const res = await apiService.getStoryTopics(selectedGenre);
            setTopics(res.topics || []);
            setStep('topic');
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenreSelect = (selectedGenre) => {
        setGenre(selectedGenre);
        loadTopics(selectedGenre);
    };

    const handleGenerate = async () => {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        try {
            // 1. Generate Content
            const genRes = await apiService.generateStory({
                genre,
                topic: selectedTopic,
                ...config
            });
            const content = genRes.content;
            setStoryContent(content);

            // 2. Save to Backend
            if (user?.id) {
                const saveRes = await apiService.saveStory(user.id, {
                    genre,
                    topic: selectedTopic,
                    content,
                    config
                });
                setCurrentStoryId(saveRes.id);
            }

            setStep('result');
        } catch (err) {
            console.error(err);
            alert('Failed to generate story.');
        } finally {
            setLoading(false);
        }
    };

    const splitIntoChunks = async () => {
        // Regex for sentence splitting
        const sentences = storyContent
            .replace(/\r?\n/g, ' ')
            .match(/[^.!?]+[.!?]?/g)
            ?.map(s => s.trim())
            .filter(s => s.length > 0) || [];

        const newChunks = [];
        for (let i = 0; i < sentences.length; i += splitSize) {
            newChunks.push({
                id: i / splitSize,
                text: sentences.slice(i, i + splitSize).join(' '),
                sentenceCount: Math.min(splitSize, sentences.length - i)
            });
        }

        // Save split status to backend
        if (currentStoryId) {
            try {
                await apiService.updateStorySplit(currentStoryId, {
                    chunks: newChunks,
                    splitSize
                });
            } catch (err) {
                console.error("Failed to save split status", err);
            }
        }

        setChunks(newChunks);
        setStep('list');
    };

    const handleAnalyzeChunk = async (chunk) => {
        setLoadingAnalysis(true);
        const user = JSON.parse(localStorage.getItem('user'));
        const sentences = chunk.text.match(/[^.!?]+[.!?]?/g)?.map(s => s.trim()) || [chunk.text];

        try {
            // 1. Check History (Full match)
            const res = await apiService.checkSentenceHistory(user?.id, chunk.text);
            if (res.found && res.data) {
                setAnalysisData(res.data);
                setAnalysisDocId(res.id); // Save Doc ID

                // Calculate Progress
                const completedCount = res.data.filter(s => s.completed).length;
                const progress = Math.round((completedCount / res.data.length) * 100);

                // Update Chunks State
                setChunks(prev => prev.map(c =>
                    c.id === chunk.id ? { ...c, progress, completedCount, totalCount: res.data.length } : c
                ));

                setLoadingAnalysis(false);
                return;
            }

            // 2. If not found, Stream Analysis
            setAnalysisData([]); // Clear previous data
            setAnalysisDocId(null);
            const accumulatedResults = [];

            await apiService.streamSentenceMatch(sentences, (data) => {
                accumulatedResults.push(data);
                // Functional update to append new data safely
                setAnalysisData(prev => [...(prev || []), data]);
            });

            // 3. Save after streaming completes
            if (user?.id && accumulatedResults.length > 0) {
                const saveRes = await apiService.saveUserSentence(user.id, accumulatedResults, chunk.text);
                if (saveRes && saveRes.data && saveRes.data.id) {
                    setAnalysisDocId(saveRes.data.id);
                }

                // Initial Progress (0%)
                setChunks(prev => prev.map(c =>
                    c.id === chunk.id ? { ...c, progress: 0, completedCount: 0, totalCount: accumulatedResults.length } : c
                ));
            }

        } catch (err) {
            console.error(err);
            alert('Analysis failed');
        } finally {
            setLoadingAnalysis(false);
        }
    };

    const handleToggleComplete = async (index) => {
        if (!analysisData || !analysisDocId) return;

        const newData = [...analysisData];
        newData[index].completed = !newData[index].completed;
        setAnalysisData(newData);

        // Update Chunks Progress
        const completedCount = newData.filter(s => s.completed).length;
        const progress = Math.round((completedCount / newData.length) * 100);

        if (selectedChunk) {
            setChunks(prev => prev.map(c =>
                c.id === selectedChunk.id ? { ...c, progress, completedCount, totalCount: newData.length } : c
            ));
        }

        try {
            await apiService.updateUserSentence(analysisDocId, newData);
        } catch (e) {
            console.error(e);
            // Revert on failure
            newData[index].completed = !newData[index].completed;
            setAnalysisData([...newData]);
        }
    };

    const handleSelectHistory = (story) => {
        setCurrentStoryId(story.id);
        setStoryContent(story.content);
        if (story.isSplit) {
            setChunks(story.chunks);
            setSplitSize(story.splitSize || 10);
            setStep('list');
        } else {
            setStep('result');
        }
    };

    const handleBack = async () => {
        if (step === 'genre') setStep('history');
        else if (step === 'topic') setStep('genre');
        else if (step === 'config') setStep('topic');
        else if (step === 'result') setStep('config');
        else if (step === 'list') {
            setStep('history');
            loadHistory(); // Refresh history
        }
        else if (step === 'detail') {
            // Save progress when leaving detail view
            if (currentStoryId && chunks.length > 0) {
                try {
                    await apiService.updateStorySplit(currentStoryId, {
                        chunks: chunks,
                        splitSize
                    });
                } catch (err) {
                    console.error("Failed to save progress", err);
                }
            }

            setStep('list');
            setAnalysisData(null);
            setSelectedChunk(null);
        }
        else navigate(-1);
    };

    // Popup logic (borrowed from FeedPage)
    const handleMeaningClick = useCallback((source, target) => {
        setPopup({ source, target });
        setWordInfo(null);
        setLoadingWord(false);
    }, []);

    const handleWordLookup = useCallback(async (word) => {
        if (!popup) return;
        setLoadingWord(true);
        const userId = JSON.parse(localStorage.getItem('user'))?.id;
        try {
            const gptRes = await apiService.wordMeaning({
                source: popup.source,
                target: popup.target,
                word: word,
                userId
            });
            const meanings = gptRes.data;
            setWordInfo({ word: word, meanings: meanings });

            // Save to History (Async)
            if (userId && meanings && meanings.length > 0) {
                const primaryMeaning = meanings[0];
                const translation = primaryMeaning.의미1 || primaryMeaning.meaning || primaryMeaning.definition || '';
                const exampleSentence = primaryMeaning.예문1 || primaryMeaning.example || primaryMeaning.exampleSentence || '';

                apiService.saveWordHistory(userId, {
                    word,
                    translation,
                    exampleSentence,
                    meanings
                }).catch(e => console.error("Failed to save word history:", e));
            }

        } catch (e) {
            console.error(e);
        } finally {
            setLoadingWord(false);
        }
    }, [popup]);

    const getStoryProgress = (story) => {
        if (!story.isSplit || !story.chunks || story.chunks.length === 0) return 0;

        const totalProgress = story.chunks.reduce((acc, chunk) => acc + (chunk.progress || 0), 0);
        return Math.round(totalProgress / story.chunks.length);
    };

    const renderContent = () => {
        if (loading && (step === 'history' || step === 'topic')) {
            return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
        }

        switch (step) {
            case 'history':
                return (
                    <div>
                        <Button onClick={() => setStep('genre')} style={{ marginBottom: '24px' }}>
                            <Plus size={20} /> Generate New Story
                        </Button>

                        {history.length === 0 ? (
                            <EmptyState>
                                <Clock size={48} />
                                <p>No story history found. Create your first story!</p>
                            </EmptyState>
                        ) : (
                            <List>
                                <Label>Recent Stories</Label>
                                {history.map((story) => {
                                    const progress = getStoryProgress(story);
                                    return (
                                        <ListItem key={story.id} onClick={() => handleSelectHistory(story)}>
                                            <ListHeader>
                                                <span>
                                                    {story.topic} ({story.genre})
                                                </span>
                                                {story.isSplit ?
                                                    <span style={{ color: '#4caf50', fontSize: '12px' }}>
                                                        {progress}% Completed
                                                    </span> :
                                                    <span style={{ color: '#888' }}>New</span>
                                                }
                                            </ListHeader>
                                            <ListText>{story.content.substring(0, 150)}...</ListText>
                                            {story.isSplit && progress > 0 && (
                                                <div style={{ marginTop: '10px', height: '4px', background: '#333', borderRadius: '2px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${progress}%`, height: '100%', background: '#4caf50' }} />
                                                </div>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </div>
                );
            case 'genre':
                return (
                    <Card>
                        <Label>Select Genre</Label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button onClick={() => handleGenreSelect('소설')} style={{ flex: 1, background: genre === '소설' ? '#e50914' : '#2a2a2a' }}>Novel</Button>
                            <Button onClick={() => handleGenreSelect('에세이')} style={{ flex: 1, background: genre === '에세이' ? '#e50914' : '#2a2a2a' }}>Essay</Button>
                        </div>
                    </Card>
                );
            case 'topic':
                return (
                    <Card>
                        <Label>Suggested Topics</Label>
                        <TopicGrid>
                            {topics.map((t, idx) => (
                                <TopicPill key={idx} $active={selectedTopic === t} onClick={() => setSelectedTopic(t)}>{t}</TopicPill>
                            ))}
                        </TopicGrid>
                        <Button disabled={!selectedTopic} onClick={() => setStep('config')}>Next <ChevronRight size={18} /></Button>
                    </Card>
                );
            case 'config':
                return (
                    <Card>
                        <Label>Mood</Label>
                        <Input value={config.mood} onChange={e => setConfig({ ...config, mood: e.target.value })} placeholder="e.g. Mysterious, Warm, Dark" />

                        <Label>Target Age</Label>
                        <Select value={config.targetAge} onChange={e => setConfig({ ...config, targetAge: e.target.value })}>
                            <option value="Child">Child</option>
                            <option value="Teen">Teen</option>
                            <option value="Adult">Adult</option>
                        </Select>

                        <Label>Spatial Background</Label>
                        <Input value={config.spatialBackground} onChange={e => setConfig({ ...config, spatialBackground: e.target.value })} placeholder="e.g. Space, Old Seoul, Island" />

                        <Label>Temporal Background</Label>
                        <Input value={config.temporalBackground} onChange={e => setConfig({ ...config, temporalBackground: e.target.value })} placeholder="e.g. 1920s, Future, Ancient Rome" />

                        <Label>Additional Keywords (Optional)</Label>
                        <Input value={config.keywords} onChange={e => setConfig({ ...config, keywords: e.target.value })} placeholder="e.g. Magic, Cat, Rain, Reunion" />

                        <Button disabled={loading} onClick={handleGenerate}>
                            {loading ? 'Generating...' : 'Generate Story'} <Send size={18} />
                        </Button>
                    </Card>
                );
            case 'result':
                return (
                    <div>
                        <Card>
                            <Title style={{ fontSize: '20px', marginBottom: '16px' }}>Story Content</Title>
                            <SplitControl>
                                <Scissors size={20} color="#e50914" />
                                <span style={{ fontSize: '14px', flex: 1 }}>Split every X sentences:</span>
                                <input
                                    type="number"
                                    value={splitSize}
                                    onChange={e => setSplitSize(parseInt(e.target.value) || 1)}
                                    style={{ width: '50px', padding: '6px', background: '#333', border: '1px solid #444', color: '#fff', borderRadius: '4px' }}
                                />
                            </SplitControl>
                            <Button onClick={splitIntoChunks} style={{ marginBottom: '24px' }}>Split & Study</Button>
                            <FullText>{storyContent}</FullText>
                        </Card>
                    </div>
                );
            case 'list':
                return (
                    <List>
                        <ListItem onClick={() => setStep('result')} style={{ marginBottom: '12px', borderColor: '#444' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '14px' }}>
                                <BookOpen size={16} /> Back to Full Story / Split settings
                            </div>
                        </ListItem>
                        {chunks.map((c, idx) => (
                            <ListItem key={idx} onClick={() => { setSelectedChunk(c); setStep('detail'); }}>
                                <ListHeader>
                                    <span>Part {idx + 1}</span>
                                    <span>{c.sentenceCount} sentences</span>
                                </ListHeader>
                                <ListText>{c.text}</ListText>
                                {c.progress !== undefined && (
                                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ flex: 1, height: '6px', background: '#333', borderRadius: '3px', overflow: 'hidden' }}>
                                            <div style={{ width: `${c.progress}%`, height: '100%', background: c.progress === 100 ? '#4caf50' : '#e50914', transition: 'width 0.3s' }} />
                                        </div>
                                        <span style={{ fontSize: '12px', color: c.progress === 100 ? '#4caf50' : '#888' }}>
                                            {c.progress}%
                                        </span>
                                    </div>
                                )}
                            </ListItem>
                        ))}
                    </List>
                );
            case 'detail':
                return (
                    <div style={{ paddingBottom: '40px' }}>
                        <Card>
                            <Button onClick={() => handleAnalyzeChunk(selectedChunk)} disabled={loadingAnalysis} style={{ background: '#333', marginBottom: '16px' }}>
                                {loadingAnalysis ? 'Analyzing...' : <><Activity size={18} /> Analyze Sentence</>}
                            </Button>
                            <FullText style={{ fontSize: '18px' }}>{selectedChunk.text}</FullText>

                            {analysisData && (
                                <AnalysisSection>
                                    {analysisData.map((item, idx) => (
                                        <AnalyzedResult
                                            key={idx}
                                            item={item}
                                            idx={idx}
                                            speak={speak}
                                            onWordClick={handleMeaningClick}
                                            onToggleComplete={handleToggleComplete}
                                        />
                                    ))}
                                </AnalysisSection>
                            )}
                        </Card>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Container>
            <Header>
                <BackButton onClick={handleBack}><ArrowLeft size={24} /></BackButton>
                <Title>MyStory</Title>
            </Header>

            {renderContent()}

            {popup && (
                <MeaningPopup
                    source={popup.source}
                    target={popup.target}
                    onClose={() => { setPopup(null); setWordInfo(null); setLoadingWord(false); }}
                    wordInfo={wordInfo}
                    onWordClick={handleWordLookup}
                    loadingWord={loadingWord}
                    onUpdateWordInfo={setWordInfo}
                />
            )}
        </Container>
    );
}

export default MyStoryPage;
